import { ethers } from 'ethers';
import transactionService from './transactionService';

/**
 * Service for interacting with the CEL Token contract
 */
class CELTokenService {
  /**
   * Create a new CELTokenService instance
   * @param {Object} contract - Ethers.js contract instance
   */
  constructor(contract) {
    this.contract = contract;
  }

  /**
   * Get the token balance of an address
   * @param {string} address - Address to check balance
   * @returns {Promise<BigNumber>} - Balance in wei
   */
  async balanceOf(address) {
    try {
      const balance = await this.contract.balanceOf(address);
      return balance;
    } catch (error) {
      console.error('Error getting CEL balance:', error);
      throw error;
    }
  }

  /**
   * Get the token name
   * @returns {Promise<string>} - Token name
   */
  async name() {
    try {
      return await this.contract.name();
    } catch (error) {
      console.error('Error getting token name:', error);
      throw error;
    }
  }

  /**
   * Get the token symbol
   * @returns {Promise<string>} - Token symbol
   */
  async symbol() {
    try {
      return await this.contract.symbol();
    } catch (error) {
      console.error('Error getting token symbol:', error);
      throw error;
    }
  }

  /**
   * Get the token decimals
   * @returns {Promise<number>} - Token decimals
   */
  async decimals() {
    try {
      return await this.contract.decimals();
    } catch (error) {
      console.error('Error getting token decimals:', error);
      throw error;
    }
  }

  /**
   * Get the total supply of tokens
   * @returns {Promise<BigNumber>} - Total supply in wei
   */
  async totalSupply() {
    try {
      return await this.contract.totalSupply();
    } catch (error) {
      console.error('Error getting total supply:', error);
      throw error;
    }
  }

  /**
   * Transfer tokens to a recipient
   * @param {string} to - Recipient address
   * @param {string|BigNumber} amount - Amount to transfer in wei
   * @returns {Promise<Object>} - Transaction result
   */
  async transfer(to, amount) {
    try {
      const tx = this.contract.transfer(to, amount);
      return await transactionService.sendTransaction(tx, {
        title: 'Transfer CEL',
        description: `Transfer ${ethers.utils.formatEther(amount)} CEL to ${to}`,
        type: 'transfer'
      });
    } catch (error) {
      console.error('Error transferring tokens:', error);
      throw error;
    }
  }

  /**
   * Approve a spender to use tokens
   * @param {string} spender - Spender address
   * @param {string|BigNumber} amount - Amount to approve in wei
   * @returns {Promise<Object>} - Transaction result
   */
  async approve(spender, amount) {
    try {
      console.log(`Approving ${ethers.utils.formatEther(amount)} CEL for ${spender}`);
      
      // Send the approval transaction directly using the contract
      const tx = await this.contract.approve(spender, amount);
      
      console.log('Approval transaction sent:', tx.hash);
      console.log('Waiting for confirmation...');
      
      // Wait for confirmation
      const receipt = await tx.wait();
      console.log('Approval confirmed in block:', receipt.blockNumber);
      
      return {
        hash: tx.hash,
        receipt: receipt,
        status: receipt.status ? 'confirmed' : 'failed'
      };
    } catch (error) {
      console.error('Error approving tokens:', error);
      throw error;
    }
  }

  /**
   * Check allowance for a spender
   * @param {string} owner - Owner address
   * @param {string} spender - Spender address
   * @returns {Promise<BigNumber>} - Allowance in wei
   */
  async allowance(owner, spender) {
    try {
      return await this.contract.allowance(owner, spender);
    } catch (error) {
      console.error('Error checking allowance:', error);
      throw error;
    }
  }

  /**
   * Format token amount from wei to a readable string
   * @param {BigNumber|string} amount - Amount in wei
   * @returns {string} - Formatted amount
   */
  async formatAmount(amount) {
    const decimals = await this.decimals();
    return ethers.utils.formatUnits(amount, decimals);
  }

  /**
   * Parse token amount from human-readable string to wei
   * @param {string} amount - Amount as a string
   * @returns {BigNumber} - Amount in wei
   */
  async parseAmount(amount) {
    const decimals = await this.decimals();
    return ethers.utils.parseUnits(amount.toString(), decimals);
  }

  /**
   * Check if user has sufficient balance
   * @param {string} address - User address
   * @param {BigNumber|string} amount - Required amount in wei
   * @returns {Promise<boolean>} - True if sufficient balance
   */
  async hasSufficientBalance(address, amount) {
    const balance = await this.balanceOf(address);
    return balance.gte(ethers.BigNumber.from(amount));
  }

  /**
   * Check if user has sufficient allowance
   * @param {string} owner - Owner address
   * @param {string} spender - Spender address
   * @param {BigNumber|string} amount - Required amount in wei
   * @returns {Promise<boolean>} - True if sufficient allowance
   */
  async hasSufficientAllowance(owner, spender, amount) {
    const allowanceAmount = await this.allowance(owner, spender);
    return allowanceAmount.gte(ethers.BigNumber.from(amount));
  }

//   /**
//    * Ensure sufficient allowance by approving if needed
//    * @param {string} spender - Spender address
//    * @param {BigNumber|string} amount - Required amount in wei
//    * @returns {Promise<boolean>} - True if approval was successful
//    */
//   async ensureAllowance(spender, amount) {
//     try {
//       console.log(`ensureAllowance called with spender: ${spender}, amount: ${ethers.utils.formatEther(amount)} CEL`);
      
//       // Get owner address from the signer
//       const owner = await this.contract.signer.getAddress();
//       console.log(`Owner address: ${owner}`);
      
//       // Check current allowance
//       const currentAllowance = await this.allowance(owner, spender);
//       console.log(`Current allowance for ${spender}: ${ethers.utils.formatEther(currentAllowance)} CEL`);
      
//       // If current allowance is sufficient, return true
//       if (currentAllowance.gte(ethers.BigNumber.from(amount))) {
//         console.log('Current allowance is sufficient, no approval needed');
//         return true;
//       }
      
//       console.log(`Approving ${ethers.utils.formatEther(amount)} CEL for ${spender}`);
      
//       // Approve the spender for the amount
//       const tx = await this.contract.approve(spender, amount);
//       console.log('Approval transaction sent:', tx.hash);
      
//       const receipt = await tx.wait();
//       console.log('Approval transaction mined:', receipt);
      
//       // Verify new allowance (to ensure it worked)
//       const newAllowance = await this.allowance(owner, spender);
//       console.log(`New allowance after approval: ${ethers.utils.formatEther(newAllowance)} CEL`);
      
//       const success = newAllowance.gte(ethers.BigNumber.from(amount));
      
//       if (!success) {
//         console.warn('Approval transaction succeeded but allowance is still insufficient!');
//       }
      
//       return success;
//     } catch (error) {
//       console.error('Error ensuring allowance:', error);
//       throw error; // Instead of returning false, throw to handle the specific error at the caller
//     }
 // }
}

export default CELTokenService; 